import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { company, categories } from "../data/site";

const nav = [
  { to: "/", label: "Ana Sayfa" },
  { to: "/kurumsal", label: "Kurumsal" },
  { to: "/urunler", label: "Ürünler", mega: true },
  { to: "/projeler", label: "Projeler" },
  { to: "/blog", label: "Blog" },
  { to: "/iletisim", label: "İletişim" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || open || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMega(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "glass text-[#1E1E1E]" : "bg-transparent text-white"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 py-4">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center relative"
        >
          <img
            src="/serapark-logo.png"
            alt="SeramikPark"
            className={`h-9 md:h-11 w-auto transition-opacity duration-500 ${solid ? "opacity-100" : "opacity-0"}`}
          />
          <img
            src="/serapark-logo-white.png"
            alt=""
            aria-hidden
            className={`h-9 md:h-11 w-auto absolute left-0 top-1/2 -translate-y-1/2 transition-opacity duration-500 ${solid ? "opacity-0" : "opacity-100"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => (
            <div
              key={n.to}
              className="relative"
              onMouseEnter={() => n.mega && setMega(true)}
              onMouseLeave={() => n.mega && setMega(false)}
            >
              <NavLink
                to={n.to}
                data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.08em] uppercase font-medium transition-colors ${
                    isActive
                      ? "text-[#B8860B]"
                      : `${solid ? "text-[#1E1E1E]" : "text-white/90"} hover:text-[#B8860B]`
                  }`
                }
              >
                {n.label}
              </NavLink>
              <AnimatePresence>
                {n.mega && mega && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.28 }}
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-6"
                  >
                    <div className="glass rounded-sm p-6 grid grid-cols-3 gap-6 w-[720px]">
                      {categories.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/urunler?kategori=${c.slug}`}
                          data-testid={`megamenu-${c.slug}`}
                          className="group flex gap-3 items-center"
                        >
                          <div className="w-14 h-14 overflow-hidden bg-[#F5F5F5] clip-frame">
                            <img
                              src={c.image}
                              alt={c.name}
                              loading="lazy"
                              className="w-full h-full object-cover img-zoom"
                            />
                          </div>
                          <div>
                            <div className="text-[13px] font-medium text-[#1E1E1E] group-hover:text-[#B8860B] transition-colors">
                              {c.name}
                            </div>
                            <div className="text-[11px] tracking-wider text-[#888]">
                              {c.count} ürün
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={company.whatsapp}
            target="_blank"
            rel="noreferrer"
            data-testid="header-whatsapp"
            className="w-9 h-9 grid place-items-center border border-[#1E1E1E]/15 hover:border-[#B8860B] hover:text-[#B8860B] transition-colors"
            aria-label="WhatsApp"
          >
            <MessageCircle size={16} />
          </a>
          <Link
            to="/teklif-al"
            data-testid="header-quote-cta"
            className="btn-gold"
          >
            Teklif Al
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 grid place-items-center text-[#1E1E1E]"
          aria-label="Menü"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-t border-black/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mnav-${n.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `text-[15px] font-medium tracking-wide ${
                      isActive ? "text-[#B8860B]" : "text-[#1E1E1E]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <div className="pt-4 border-t border-black/5 flex gap-3">
                <a
                  href={`tel:${company.phoneRaw}`}
                  className="flex-1 btn-ghost justify-center text-center"
                >
                  <Phone size={14} /> {company.phone}
                </a>
                <Link
                  to="/teklif-al"
                  className="flex-1 btn-gold justify-center text-center"
                >
                  Teklif Al
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
